import { Loader, ChevronRight, ChevronDown, Import, FilePlus, FileInput, Info, Eye, Download } from "lucide-react";
import type { WorkspaceFile, FileVersion } from "../lib/types";
import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDateTime } from '../lib/text';
import { getFileIcon, MetadataInfo, downloadResponseAsFile } from '../lib/files';
import { sourceLabel } from '../lib/sources';
import DocTypeCombobox, { type DocTypeOption } from '../components/DocTypeCombobox';
import DocTypeLinkRow from '../components/DocTypeLinkRow';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import BackButton from '../components/BackButton';
import LinkedBadge from '../components/LinkedBadge';
import Tabs from '../components/Tabs';
import SourceLogo from '../components/SourceLogo';

export default function FileDetailPage() {
    const { authFetch, isAdmin } = useAuth();
    const { showToast } = useToast();
    const location = useLocation();

    const [selectedFile, setSelectedFile] = useState<WorkspaceFile | null>(null);
    const [versions, setVersions] = useState<FileVersion[]>([]);
    const [selectedVersionNum, setSelectedVersionNum] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [allDocTypes, setAllDocTypes] = useState<DocTypeOption[]>([]);
    const [activeTab, setActiveTab] = useState<'overview' | 'preview'>('overview');
    const [embed, setEmbed] = useState<{ kind: string; url?: string } | null>(null);
    const [embedLoading, setEmbedLoading] = useState<boolean>(false);
    // Fetched lazily on first Preview open and kept, since the Preview tab stays mounted.
    const [previewOpened, setPreviewOpened] = useState<boolean>(false);
    // Drive uses the viewer's own session, so access is probed via the thumbnail first.
    // null = not applicable; else checking | ok | denied.
    const [driveProbe, setDriveProbe] = useState<'checking' | 'ok' | 'denied' | null>(null);

    const importing = activeJobId !== null;
    const isGenerated = !!selectedFile?.uri?.startsWith('generated://');
    const isSnapshot = selectedFile?.source_metadata?.snapshot === true;

    const pathParts = location.pathname.split('/').filter(p => p);
    const fileId = pathParts[1];

    const handleReimport = async () => {
        if (!fileId || activeJobId) return;
        try {
            const res = await authFetch(`/files/${fileId}/reimport`, { method: 'POST' });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to start re-import');
            }
            const data = await res.json();
            if (data.job_id) setActiveJobId(data.job_id);
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to start import', 'error');
        }
    };

    const reloadFile = async () => {
        if (!fileId) return;
        const res = await authFetch(`/files/${fileId}`);
        if (res.ok) setSelectedFile(await res.json());
    };

    const handleLinkType = async (definitionId: string) => {
        const res = await authFetch(`/workspace/links/${definitionId}/${fileId}`, { method: 'POST' });
        if (!res.ok) {
            showToast('Failed to link', 'error');
            return;
        }
        await reloadFile();
    };

    useEffect(() => {
        let stale = false;
        authFetch('/workspace/types')
            .then(r => r.ok ? r.json() : [])
            .then((data: DocTypeOption[]) => { if (!stale) setAllDocTypes(data); })
            .catch(() => { if (!stale) setAllDocTypes([]); });
        return () => { stale = true; };
    }, []);

    const handleUnlinkType = async (definitionId: string) => {
        if (!window.confirm('Unlink this file from the document type?')) return;
        const res = await authFetch(`/workspace/links/${definitionId}/${fileId}`, { method: 'DELETE' });
        if (!res.ok) {
            showToast('Failed to unlink', 'error');
            return;
        }
        await reloadFile();
    };

    useEffect(() => {
        if (!activeJobId || !fileId) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await authFetch(`/jobs/${activeJobId}`);
                if (!res.ok) return;
                const job = await res.json();
                if (cancelled) return;
                if (job.status === 'success' || job.status === 'failed') {
                    if (job.status === 'failed') {
                        showToast(job.error || job.message || 'Import failed', 'error');
                    } else {
                        const [docResp, versionsResp] = await Promise.all([
                            authFetch(`/files/${fileId}`),
                            authFetch(`/files/${fileId}/versions`),
                        ]);
                        if (cancelled) return;
                        if (docResp.ok) setSelectedFile(await docResp.json());
                        if (versionsResp.ok) {
                            const list: FileVersion[] = await versionsResp.json();
                            setVersions(list);
                            if (list.length > 0) setSelectedVersionNum(list[0].version);
                        }
                        showToast('File imported', 'success');
                    }
                    setActiveJobId(null);
                }
            } catch {}
        };
        tick();
        const interval = setInterval(tick, 2000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [activeJobId, fileId]);

    useEffect(() => {
        const loadFile = async () => {
            if (!fileId) return;

            setSelectedFile(null);
            setLoading(true);
            setError(null);
            setPreviewOpened(false);

            try {
                const [docResp, versionsResp] = await Promise.all([
                    authFetch(`/files/${fileId}`),
                    authFetch(`/files/${fileId}/versions`),
                ]);
                if (!docResp.ok) throw new Error('Failed to load file');
                const fullDoc = await docResp.json();
                setSelectedFile(fullDoc);
                let versionsList: FileVersion[] = [];
                if (versionsResp.ok) {
                    versionsList = await versionsResp.json();
                    setVersions(versionsList);
                }
                if (versionsList.length > 0) {
                    setSelectedVersionNum(versionsList[0].version);
                }
                // Resume polling if a task is already in flight (another tab, user or background fan-out).
                if (fullDoc.active_job) setActiveJobId(fullDoc.active_job.id);
                setLoading(false);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        };
        loadFile();
    }, [fileId]);

    const activeVersion = useMemo(
        () => versions.find(v => v.version === selectedVersionNum) ?? versions[0] ?? null,
        [versions, selectedVersionNum]
    );

    useEffect(() => { if (activeTab === 'preview') setPreviewOpened(true); }, [activeTab]);

    useEffect(() => {
        const snap = selectedFile?.source_metadata?.snapshot === true;
        if (!fileId || !selectedFile || snap || !previewOpened || activeJobId) {
            setEmbed(null);
            return;
        }
        let cancelled = false;
        setEmbedLoading(true);
        authFetch(`/files/${fileId}/preview-url`)
            .then(r => r.ok ? r.json() : { kind: 'none' })
            .then(d => { if (!cancelled) setEmbed(d); })
            .catch(() => { if (!cancelled) setEmbed({ kind: 'none' }); })
            .finally(() => { if (!cancelled) setEmbedLoading(false); });
        return () => { cancelled = true; };
    }, [fileId, selectedFile, activeVersion, previewOpened, activeJobId, authFetch]);

    useEffect(() => {
        const id = selectedFile?.source_metadata?.id;
        if (embed?.kind !== 'iframe' || selectedFile?.source_type !== 'drive' || !id) {
            setDriveProbe(null);
            return;
        }
        setDriveProbe('checking');
        let settled = false;
        const img = new Image();
        img.onload = () => { if (!settled) { settled = true; setDriveProbe('ok'); } };
        img.onerror = () => { if (!settled) { settled = true; setDriveProbe('denied'); } };
        img.src = `https://drive.google.com/thumbnail?id=${id}&sz=w400`;
        return () => { settled = true; };
    }, [embed, selectedFile]);

    const downloadRaw = async () => {
        if (!fileId) return;
        const res = await authFetch(`/files/${fileId}/raw`);
        if (res.ok) await downloadResponseAsFile(res, selectedFile?.source_filename || 'document');
        else showToast('Could not download the file', 'error');
    };

    if (loading) return <PageLoader label="Loading file..." />;
    if (error || !selectedFile) return <PageError title="Failed to load file" message={error || 'File not found'} />;

    const embedUrl = !isSnapshot && embed?.kind === 'iframe' ? embed.url : undefined;
    const embedLabel = sourceLabel(selectedFile.source_type);
    // SharePoint/OneDrive are server-authed; Drive must pass the viewer-access probe first.
    const isDriveEmbed = !!embedUrl && selectedFile.source_type === 'drive';
    const embedChecking = isDriveEmbed && driveProbe === 'checking';
    const showEmbed = !!embedUrl && (!isDriveEmbed || driveProbe === 'ok');
    const previewAvailable = isSnapshot ? !!selectedFile.source_metadata : true;
    const effectiveTab =
        activeTab === 'preview' && !previewAvailable ? 'overview'
        : activeTab;
    const hasStoredVersion = !!activeVersion;
    const title = activeVersion?.title || selectedFile.source_filename || selectedFile.uri;
    const isLatest = activeVersion?.version === versions[0]?.version;
    const linkedTo = selectedFile.linked_to ?? [];
    const candidateFor = selectedFile.candidate_for ?? [];

    // Surface the ?type= doc type as a candidate unless it's already linked or a candidate.
    const typeParam = new URLSearchParams(location.search).get('type');
    const candidateForShown = (() => {
        if (!typeParam) return candidateFor;
        if (linkedTo.some(l => l.definition_id === typeParam)) return candidateFor;
        if (candidateFor.some(c => c.definition_id === typeParam)) return candidateFor;
        const dt = allDocTypes.find(t => t.id === typeParam);
        if (!dt) return candidateFor;
        return [
            {
                definition_id: dt.id,
                document_type_name: dt.name,
                section_id: dt.section_id,
                section_name: dt.section_name,
                score: 0,
            },
            ...candidateFor,
        ];
    })();

    const metadata: Record<string, any> = {
        'MIME type': selectedFile.mime_type,
        URI: selectedFile.uri,
    };
    if (selectedFile.source_filename) metadata['Source filename'] = selectedFile.source_filename;
    if (selectedFile.source_path) metadata['Source path'] = selectedFile.source_path;
    if (activeVersion) metadata['Version'] = `v${activeVersion.version} - ${formatDateTime(activeVersion.created_at)}`;
    if (versions.length > 1) metadata['Total versions'] = versions.length;
    if (selectedFile.source_metadata) metadata['Source metadata'] = selectedFile.source_metadata;
    if (activeVersion?.full_text) metadata['Content'] = activeVersion.full_text;

    const FileIconCmp = getFileIcon(selectedFile.mime_type);

    return (
        <div>
            <div className="pb-6 mb-3 border-b border-softgrey">
                <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                        <BackButton className="mt-3" />
                        <div className="navaris-tile-tint w-12 h-12 rounded-xl flex border border-softgrey items-center justify-center shrink-0">
                            <FileIconCmp className="w-6 h-6 text-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-graphite wrap-break-word">{title}</h3>
                                {linkedTo.length > 0 && <LinkedBadge />}
                                <MetadataInfo metadata={metadata} />
                            </div>
                            {selectedFile.source && selectedFile.source_type && selectedFile.source_id && (
                                <div className="flex items-center flex-wrap gap-1.5 mt-1 text-sm text-coolslate min-w-0">
                                    <Link
                                        to={`/connections/${selectedFile.source_id}`}
                                        className="inline-flex items-center gap-1.5 hover:text-brand-dark transition-colors"
                                    >
                                        <SourceLogo type={selectedFile.source_type} size="sm" />
                                        {selectedFile.source}
                                    </Link>
                                    {selectedFile.source_path &&
                                        selectedFile.source_path.split('/').filter(Boolean).map((segment, idx, arr) => {
                                            const folderPath = arr.slice(0, idx + 1).join('/');
                                            return (
                                                <span key={idx} className="inline-flex items-center gap-1.5">
                                                    <ChevronRight className="w-3.5 h-3.5 text-coolslate/60" />
                                                    <Link
                                                        to={`/connections/${selectedFile.source_id}?folder=${encodeURIComponent(folderPath)}`}
                                                        className="hover:text-brand-dark transition-colors"
                                                        title={folderPath}
                                                    >
                                                        {segment}
                                                    </Link>
                                                </span>
                                            );
                                        })
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {hasStoredVersion && !isSnapshot && (
                            <button
                                type="button"
                                onClick={downloadRaw}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-lg border border-softgrey hover:bg-offwhite transition-colors"
                                title="Download this document"
                            >
                                <Download className="w-4 h-4" /> Download
                            </button>
                        )}
                        {versions.length > 1 && (
                            <div className="relative">
                                <select
                                    value={selectedVersionNum ?? ''}
                                    onChange={e => setSelectedVersionNum(Number(e.target.value))}
                                    className="appearance-none pl-3 pr-8 py-2 text-[13px] font-medium border border-softgrey rounded-lg bg-white text-graphite cursor-pointer hover:border-brand/40 transition-colors"
                                >
                                    {versions.map(v => (
                                        <option key={v.version} value={v.version}>
                                            v{v.version}{v.version === versions[0].version ? ' (latest)' : ''} - {formatDateTime(v.created_at)}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="w-3.5 h-3.5 text-coolslate absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                        )}
                        {versions.length === 1 && (
                            <span className="text-xs font-medium text-coolslate bg-offwhite border border-softgrey rounded px-2 py-1.5">
                                v{versions[0].version}
                            </span>
                        )}
                    </div>
                </div>
                {!isLatest && activeVersion && (
                    <div className="mt-3 text-xs text-coolslate bg-offwhite border border-softgrey rounded-md px-3 py-2 inline-block">
                        Viewing version {activeVersion.version} from {formatDateTime(activeVersion.created_at)}
                    </div>
                )}
            </div>

            <Tabs
                items={[
                    { id: 'overview' as const, label: 'Overview', icon: Info },
                    { id: 'preview' as const, label: 'Preview', icon: Eye, disabled: !previewAvailable, disabledTitle: 'No preview available' },
                ]}
                active={effectiveTab}
                onChange={setActiveTab}
            />

            {/* Kept mounted (hidden when inactive) so tab switches keep the Preview. */}
            <div className={effectiveTab === 'overview' ? undefined : 'hidden'}>
            <div className="flex flex-wrap items-center gap-5 mb-6 text-sm text-coolslate">
                <span className="inline-flex items-center gap-1.5"><FilePlus className="w-4 h-4 text-brand" />{formatDateTime(selectedFile.source_created_at || selectedFile.created_at)}</span>
                <span className="inline-flex items-center gap-1.5"><FileInput className="w-4 h-4 text-brand" />{formatDateTime(selectedFile.source_modified_at || selectedFile.updated_at)}</span>
            </div>

            {!isSnapshot && (
                <>
                    <div className="mb-4">
                        <h4 className="navaris-h3">Document types</h4>
                        <p className="text-[13px] text-coolslate mt-1">
                            Document types this file is linked to or is a candidate for.
                        </p>
                        {selectedFile?.owner_user_id && (
                            <p className="text-[13px] text-graphite mt-2 px-3 py-2 rounded-lg bg-offwhite border border-softgrey">
                                Private to you. Filing it under a document type shares it with the workspace,
                                so it can be searched by everyone and read into the company record.
                            </p>
                        )}
                        {selectedFile?.open_review_id && (
                            <Link
                                to={`/review?id=${selectedFile.open_review_id}`}
                                className="mt-2 flex items-center justify-between gap-2 text-[13px] px-3 py-2 rounded-lg border border-dashed border-state-warning bg-state-warning/15 text-graphite hover:bg-state-warning/30"
                            >
                                What this document would add to the record is waiting for review.
                                <span className="text-brand-dark font-medium shrink-0">Review</span>
                            </Link>
                        )}
                    </div>
                    <div className="mb-6 space-y-3">
                        <DocTypeCombobox
                            mode="combobox"
                            allTypes={allDocTypes}
                            excludeDefinitions={new Set(linkedTo.map(l => l.definition_id))}
                            onLink={handleLinkType}
                            canCreate={isAdmin}
                        />
                    </div>
                    {(linkedTo.length > 0 || candidateForShown.length > 0) && (
                        <div className="mb-6 space-y-6">
                            {linkedTo.length > 0 && (
                                <div>
                                    <div className="space-y-1.5">
                                        {linkedTo.map(l => (
                                            <DocTypeLinkRow
                                                key={`${l.section_id}:${l.definition_id}`}
                                                definitionId={l.definition_id}
                                                name={l.document_type_name}
                                                sectionName={l.section_name}
                                                kind="linked"
                                                onAction={() => handleUnlinkType(l.definition_id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {candidateForShown.length > 0 && (
                                <div>
                                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                        {candidateForShown.map(c => (
                                            <DocTypeLinkRow
                                                key={c.definition_id}
                                                definitionId={c.definition_id}
                                                name={c.document_type_name}
                                                sectionName={c.section_name}
                                                kind="candidate"
                                                onAction={() => handleLinkType(c.definition_id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            </div>

            {/* Kept mounted (hidden when inactive) so the iframe survives tab switches. */}
            {previewAvailable && (
                <div className={effectiveTab === 'preview' ? undefined : 'hidden'}>
                    {isSnapshot ? (
                        (() => {
                            const { snapshot: _snapshotFlag, ...record } = selectedFile.source_metadata!;
                            return (
                                <pre className="bg-offwhite border border-softgrey rounded-lg p-4 text-xs text-graphite overflow-auto whitespace-pre-wrap wrap-break-word">
                                    {JSON.stringify(record, null, 2)}
                                </pre>
                            );
                        })()
                    ) : (embedLoading || embedChecking) ? (
                        <div className="flex justify-center py-16">
                            <Loader className="w-8 h-8 animate-spin text-brand" />
                        </div>
                    ) : showEmbed ? (
                        <div>
                            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-medium text-coolslate">
                                {selectedFile.source_type && (
                                    <SourceLogo type={selectedFile.source_type} size="xs" alt="" />
                                )}
                                Previewing in {embedLabel}
                            </div>
                            <iframe
                                src={embedUrl}
                                title={`Preview of ${title}`}
                                className="w-full h-[80vh] rounded-lg border border-softgrey bg-white"
                            />
                        </div>
                    ) : hasStoredVersion ? (
                        <div className="border border-softgrey rounded-xl bg-offwhite p-10 text-center">
                            <p className="text-sm text-coolslate mb-4">Download this document to open it in your own app.</p>
                            <button
                                onClick={downloadRaw}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer"
                            >
                                <Download className="w-4 h-4" /> Download
                            </button>
                        </div>
                    ) : isGenerated ? (
                        <div className="border border-softgrey rounded-xl bg-offwhite p-10 text-center">
                            <p className="text-sm text-coolslate">
                                This document hasn't been generated yet. Generate it from its connection or document type.
                            </p>
                        </div>
                    ) : (
                        <div className="border border-softgrey rounded-xl bg-offwhite p-10 text-center">
                            <p className="text-sm text-coolslate mb-4">This document hasn't been read in yet.</p>
                            <button
                                onClick={handleReimport}
                                disabled={importing}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {importing ? <Loader className="w-4 h-4 animate-spin" /> : <Import className="w-4 h-4" />}
                                {importing ? 'Reading in…' : 'Read this document in'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
